
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Plus, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DocumentContentEditor } from "./DocumentContentEditor";

interface ProposalGeneratorViewProps {
  content: any;
  documentId: string;
  onUpdate: () => void;
}

interface ProposalSection {
  id: string;
  title: string;
  content: string;
}

export function ProposalGeneratorView({ content, documentId, onUpdate }: ProposalGeneratorViewProps) {
  const [sections, setSections] = useState<ProposalSection[]>(content.sections || []);
  const [logoUrl, setLogoUrl] = useState<string>(content.logoUrl || "");
  const { toast } = useToast();

  const addSection = () => {
    const newSection: ProposalSection = {
      id: Date.now().toString(),
      title: "",
      content: ""
    };
    const updatedSections = [...sections, newSection];
    setSections(updatedSections);
    updateContent({ sections: updatedSections });
  };

  const updateSection = (id: string, field: keyof ProposalSection, value: string) => {
    const updatedSections = sections.map(section => 
      section.id === id ? { ...section, [field]: value } : section
    );
    setSections(updatedSections);
    updateContent({ sections: updatedSections });
  };

  const removeSection = (id: string) => {
    const updatedSections = sections.filter(section => section.id !== id);
    setSections(updatedSections);
    updateContent({ sections: updatedSections });
  };

  const updateContent = async (updates: any) => {
    try {
      const updatedContent = { ...content, ...updates };
      
      const { error } = await supabase
        .from("documents")
        .update({ 
          content: updatedContent,
          updated_at: new Date().toISOString()
        })
        .eq("id", documentId);

      if (error) throw error;
      onUpdate();
    } catch (error) {
      console.error("Error updating proposal:", error);
      toast({
        title: "Error",
        description: "Failed to update proposal. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleContentUpdate = (field: string) => (newContent: string) => {
    updateContent({ [field]: newContent });
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/proposal-logo-${Date.now()}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('user-logos')
        .upload(fileName, file);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('user-logos')
        .getPublicUrl(fileName);

      setLogoUrl(publicUrl);
      updateContent({ logoUrl: publicUrl });

      await supabase.from('user_logos').insert({
        user_id: user.id,
        file_name: file.name,
        file_path: fileName,
        file_size: file.size,
        mime_type: file.type
      });

      toast({
        title: "Logo uploaded successfully",
        description: "Your logo has been added to the proposal template.",
      });
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: "Upload failed",
        description: "Failed to upload logo. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <FileText className="h-6 w-6 text-blue-600" />
        <h2 className="text-2xl font-bold">Proposal Generator</h2>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Proposal Header</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label htmlFor="proposalLogo">Company Logo</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="proposalLogo"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="flex-1"
                />
              </div>
              {logoUrl && (
                <div className="mt-2">
                  <img src={logoUrl} alt="Company Logo" className="h-16 w-auto" />
                </div>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="proposalTitle">Proposal Title</Label>
              <Input
                id="proposalTitle"
                placeholder="Project Proposal Title"
                value={content.proposalTitle || ""}
                onChange={(e) => updateContent({ proposalTitle: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="proposalDate">Date</Label>
              <Input
                id="proposalDate"
                type="date"
                value={content.proposalDate || ""}
                onChange={(e) => updateContent({ proposalDate: e.target.value })}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="companyName">Your Company</Label>
              <Input
                id="companyName"
                placeholder="Your Company Name"
                value={content.companyName || ""}
                onChange={(e) => updateContent({ companyName: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="clientName">Client Name</Label>
              <Input
                id="clientName"
                placeholder="Client or Company Name"
                value={content.clientName || ""}
                onChange={(e) => updateContent({ clientName: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Executive Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <DocumentContentEditor
            section="Executive Summary"
            content={content.executiveSummary || ""}
            documentId={documentId}
            field="executiveSummary"
            onUpdate={handleContentUpdate("executiveSummary")}
            showHint={true}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Project Overview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Project Description</Label>
            <DocumentContentEditor
              section="Project Description"
              content={content.projectDescription || ""}
              documentId={documentId}
              field="projectDescription"
              onUpdate={handleContentUpdate("projectDescription")}
              showHint={true}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="timeline">Timeline</Label>
              <Input
                id="timeline"
                placeholder="e.g., 8-12 weeks"
                value={content.timeline || ""}
                onChange={(e) => updateContent({ timeline: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="budget">Budget</Label>
              <Input
                id="budget"
                placeholder="e.g., $25,000 - $35,000"
                value={content.budget || ""}
                onChange={(e) => updateContent({ budget: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Proposal Sections</CardTitle>
            <Button onClick={addSection} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Section
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {sections.map((section, index) => (
            <Card key={section.id} className="p-4">
              <div className="flex justify-between items-start mb-4">
                <h4 className="font-semibold">Section {index + 1}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeSection(section.id)}
                  className="text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor={`title-${section.id}`}>Section Title</Label>
                  <Input
                    id={`title-${section.id}`}
                    placeholder="e.g., Methodology, Deliverables, Terms"
                    value={section.title}
                    onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                  />
                </div>
                
                <div>
                  <Label>Section Content</Label>
                  <DocumentContentEditor
                    section={section.title || `Section ${index + 1}`}
                    content={section.content || ""}
                    documentId={documentId}
                    field={`section_${section.id}`}
                    onUpdate={(newContent) => updateSection(section.id, 'content', newContent)}
                    showHint={true}
                  />
                </div>
              </div>
            </Card>
          ))}
          
          {sections.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              No sections added yet. Click "Add Section" to get started.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pricing & Terms</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Pricing Structure</Label>
            <DocumentContentEditor
              section="Pricing Structure"
              content={content.pricingStructure || ""}
              documentId={documentId}
              field="pricingStructure"
              onUpdate={handleContentUpdate("pricingStructure")}
              showHint={true}
            />
          </div>
          
          <div>
            <Label>Terms & Conditions</Label>
            <DocumentContentEditor
              section="Terms & Conditions"
              content={content.termsConditions || ""}
              documentId={documentId}
              field="termsConditions"
              onUpdate={handleContentUpdate("termsConditions")}
              showHint={true}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Next Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <DocumentContentEditor
            section="Next Steps"
            content={content.nextSteps || ""}
            documentId={documentId}
            field="nextSteps"
            onUpdate={handleContentUpdate("nextSteps")}
            showHint={true}
          />
        </CardContent>
      </Card>
    </div>
  );
}
